import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import SectionHeader from '../components/SectionHeader';
import { fmt, fmtCost } from '../utils/format';

// Below this, a line is flagged. Matches the threshold Product Margin Watch
// already uses, so the two pages agree on what "thin" means.
const LOW_GP = 30;

const gpClass = (gp) => {
  if (gp == null) return '';
  if (gp < LOW_GP) return 'neg';
  return '';
};

const pct = (v) => (v == null ? '—' : `${v}%`);

const num = (v) => (v === '' || v == null ? null : Number(v));

// app-header__btn cannot be used on this page: it is built for the dark navy
// header (white text on a translucent background) and renders invisible
// against white -- which is exactly what happened to both the Load and Queue
// Change buttons, present in the DOM but unreadable.
const primaryBtn = (disabled) => ({
  background: disabled ? '#9CA3AF' : '#1B2A4A',
  color:      '#FFFFFF',
  border:     'none',
  padding:    '7px 18px',
  borderRadius: 6,
  fontWeight: 600,
  fontSize:   13,
  cursor:     disabled ? 'not-allowed' : 'pointer',
});

// ── Price change modal ────────────────────────────────────────────────────────
const PriceModal = ({ row, staged, onClose, onSaved }) => {
  // Reopening a row shows what you staged, not Cin7's original values.
  const [retail, setRetail] = useState(staged?.new_retail_nzd ?? row.retail_nzd ?? '');
  const [aud, setAud]       = useState(staged?.new_price_aud ?? row.price_aud ?? '');
  const [ws1, setWs1]       = useState(staged?.new_wholesale1_nzd ?? row.wholesale1_nzd ?? '');
  const [ws2, setWs2]       = useState(staged?.new_wholesale2_nzd ?? row.wholesale2_nzd ?? '');
  const [err, setErr]       = useState('');

  const cost = Number(row.cost_nzd) || 0;

  // Staff is derived, never typed -- same formula the price approval dialog
  // uses, so a price set here and one set from an invoice stay consistent.
  const staff = retail !== '' && cost > 0 ? Math.round(cost * 1.15 * 1.1 * 100) / 100 : null;

  // Live GP for whatever is currently in the retail box.
  const gpOf = (price) => {
    const p = Number(price);
    if (!cost || !p) return null;
    return Math.round((1 - (cost * 1.15) / p) * 1000) / 10;
  };

  // Staged locally, not written. Nothing reaches the queue until the whole
  // batch is committed -- see the commit handler on the page for why.
  const stage = () => {
    setErr('');

    // Only carry what actually changed -- a null tells the executor to leave
    // that price alone rather than rewriting it with what was on screen.
    const changed = (next, prev) =>
      num(next) != null && Number(next) !== Number(prev ?? NaN) ? num(next) : null;

    const payload = {
      option_id:           row.option_id,
      code:                row.code,
      cost_nzd_at_request: row.cost_nzd,
      old_retail_nzd:      row.retail_nzd,
      old_price_aud:       row.price_aud,
      old_wholesale1_nzd:  row.wholesale1_nzd,
      old_wholesale2_nzd:  row.wholesale2_nzd,
      old_staff_nzd:       row.staff_nzd,
      new_retail_nzd:      changed(retail, row.retail_nzd),
      new_price_aud:       changed(aud, row.price_aud),
      new_wholesale1_nzd:  changed(ws1, row.wholesale1_nzd),
      new_wholesale2_nzd:  changed(ws2, row.wholesale2_nzd),
      new_staff_nzd:       changed(retail, row.retail_nzd) != null ? staff : null,
    };

    const nothing = ['new_retail_nzd', 'new_price_aud', 'new_wholesale1_nzd', 'new_wholesale2_nzd']
      .every((k) => payload[k] == null);
    if (nothing) {
      setErr('Nothing changed.');
      return;
    }

    onSaved(row.code, payload);
  };

  const field = (label, value, setter, suggestions) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, color: '#888', marginBottom: 3 }}>{label}</label>
      <input
        value={value}
        onChange={(e) => setter(e.target.value)}
        style={{ padding: '5px 8px', fontSize: 12, width: 140 }}
      />
      {suggestions && cost > 0 && (
        <span style={{ marginLeft: 10, fontSize: 11 }}>
          {[['40%', row.suggested_40], ['45%', row.suggested_45], ['50%', row.suggested_50]].map(([l, v]) => (
            <button
              key={l}
              type="button"
              onClick={() => setter(String(v))}
              className="action-btn action-btn--ghost"
              style={{ fontSize: 10, padding: '1px 7px', marginRight: 4 }}
            >
              {l} · {fmtCost(v)}
            </button>
          ))}
        </span>
      )}
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{ background: '#fff', borderRadius: 8, padding: 24, minWidth: 460, maxWidth: 560 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{row.code}</div>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 14 }}>{row.name}</div>

        <div style={{ fontSize: 12, marginBottom: 14 }}>
          Cost <strong>{fmtCost(row.cost_nzd)}</strong> (excl GST)
          {staff != null && <> · Staff will be set to <strong>{fmtCost(staff)}</strong></>}
        </div>

        {field(`Retail NZD${gpOf(retail) != null ? ` — ${gpOf(retail)}% GP` : ''}`, retail, setRetail, true)}
        {field('Price AUD', aud, setAud, false)}
        {field(`Wholesale 1${gpOf(ws1) != null ? ` — ${gpOf(ws1)}% GP` : ''}`, ws1, setWs1, false)}
        {field(`Wholesale 2${gpOf(ws2) != null ? ` — ${gpOf(ws2)}% GP` : ''}`, ws2, setWs2, false)}

        {err && <div style={{ color: '#c00', fontSize: 12, marginBottom: 10 }}>{err}</div>}

        <div style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>
          Held until you commit all changes — nothing is sent to Cin7 yet.
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="action-btn action-btn--ghost" onClick={onClose}>Cancel</button>
          <button onClick={stage} style={primaryBtn(false)}>Apply</button>
        </div>
      </div>
    </div>
  );
};

// ── Results table ─────────────────────────────────────────────────────────────
const MarginTable = ({ rows, loading, searched, onChange, staged }) => {
  if (loading)  return <div className="loading-row">Loading...</div>;
  if (!searched) return <div className="empty-row">Enter a PO number or item code above to begin.</div>;
  if (!rows.length) return <div className="empty-row">Nothing found for that search.</div>;

  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th></th>
            <th>Code</th>
            <th>Product</th>
            {/* Only populated in PO mode -- what this PO actually paid, which
                may differ from the cost currently held against the product. */}
            <th className="num">PO Price</th>
            <th className="num">Cost NZD</th>
            <th className="num">Retail NZD</th>
            <th className="num">GP%</th>
            <th className="num">Staff NZD</th>
            <th className="num">Price AUD</th>
            <th className="num">Wholesale 1</th>
            <th className="num">GP%</th>
            <th className="num">Wholesale 2</th>
            <th className="num">GP%</th>
            <th className="num">40% GP</th>
            <th className="num">45% GP</th>
            <th className="num">50% GP</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            // A PO price above the recorded cost means the cost on file is
            // stale, and every margin shown to its right is overstated.
            const costStale = r.po_price != null && r.cost_nzd != null
              && Math.abs(Number(r.po_price) - Number(r.cost_nzd)) > 0.01;
            return (
              <tr key={r.code} className="data-row">
                <td>
                  {r.option_id ? (
                    <button
                      className="action-btn action-btn--ghost"
                      style={{ fontSize: 10, padding: '2px 10px',
                               fontWeight: staged[r.code] ? 700 : 400 }}
                      onClick={() => onChange(r)}
                    >
                      {staged[r.code] ? 'Edited' : 'Change'}
                    </button>
                  ) : null}
                </td>
                <td><span className="code-badge">{r.code}</span></td>
                <td className="product-name">{r.name || '—'}</td>
                <td className={`num ${costStale ? 'neg' : ''}`}>
                  {r.po_price == null ? '—' : fmtCost(r.po_price)}
                </td>
                <td className="num">{fmtCost(r.cost_nzd)}</td>
                <td className="num bold">{fmtCost(r.retail_nzd)}</td>
                <td className={`num bold ${gpClass(r.gp_retail)}`}>{pct(r.gp_retail)}</td>
                <td className="num">{fmtCost(r.staff_nzd)}</td>
                <td className="num">{fmtCost(r.price_aud)}</td>
                <td className="num">{fmtCost(r.wholesale1_nzd)}</td>
                <td className={`num ${gpClass(r.gp_wholesale1)}`}>{pct(r.gp_wholesale1)}</td>
                <td className="num">{fmtCost(r.wholesale2_nzd)}</td>
                <td className={`num ${gpClass(r.gp_wholesale2)}`}>{pct(r.gp_wholesale2)}</td>
                <td className="num" style={{ color: '#888' }}>{fmtCost(r.suggested_40)}</td>
                <td className="num" style={{ color: '#888' }}>{fmtCost(r.suggested_45)}</td>
                <td className="num" style={{ color: '#888' }}>{fmtCost(r.suggested_50)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────
const MarginCheck = () => {
  const navigate = useNavigate();
  const [mode, setMode]         = useState('po');   // 'po' | 'code'
  const [term, setTerm]         = useState('');
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [searched, setSearched] = useState(false);
  const [note, setNote]         = useState('');
  const [editing, setEditing]   = useState(null);
  const [staged, setStaged]     = useState({});   // code -> queue payload
  const [committing, setCommitting] = useState(false);
  const [commitMsg, setCommitMsg]   = useState('');

  const search = useCallback(async () => {
    const q = term.trim();
    if (!q) return;
    setLoading(true);
    setNote('');

    try {
      if (mode === 'code') {
        // Partial match: staff rarely have the full code to hand, and a
        // trailing-wildcard search finds a family (e.g. all GL-86443*).
        const { data } = await supabase
          .from('v_margin_check')
          .select('*')
          .ilike('code', `%${q}%`)
          .order('code')
          .limit(100);
        setRows(data || []);
      } else {
        // Accept "PO-383499" or bare "383499" -- staff read them both ways.
        const ref = q.toUpperCase().startsWith('PO-') ? q.toUpperCase() : `PO-${q}`;

        const { data: hdr } = await supabase
          .from('purchase_order_header')
          .select('order_id, order_ref')
          .eq('order_ref', ref)
          .limit(1);

        if (!hdr || !hdr.length) {
          setRows([]);
          setNote(`No purchase order found matching ${ref}`);
          setSearched(true);
          setLoading(false);
          return;
        }

        const { data: items } = await supabase
          .from('purchase_order_items')
          .select('item_code, item_price, item_qty')
          .eq('order_id', hdr[0].order_id);

        const codes = [...new Set((items || []).map(i => i.item_code).filter(Boolean))];
        if (!codes.length) {
          setRows([]);
          setNote(`${ref} has no line items.`);
          setSearched(true);
          setLoading(false);
          return;
        }

        const { data: margins } = await supabase
          .from('v_margin_check')
          .select('*')
          .in('code', codes)
          .order('code');

        // Carry the PO's own line price through, so a stale cost is visible
        // rather than having to be inferred.
        const priceByCode = {};
        for (const i of (items || [])) {
          if (i.item_code && priceByCode[i.item_code] == null) priceByCode[i.item_code] = i.item_price;
        }
        const merged = (margins || []).map(m => ({ ...m, po_price: priceByCode[m.code] ?? null }));

        // Codes on the PO with no product record at all still deserve a row --
        // silently dropping them would hide exactly the ones needing attention.
        const found = new Set(merged.map(m => m.code));
        for (const c of codes) {
          if (!found.has(c)) {
            merged.push({ code: c, name: '(not found in product options)', po_price: priceByCode[c] ?? null });
          }
        }

        setRows(merged);
        setNote(`${ref} — ${merged.length} line(s)`);
      }
    } finally {
      setSearched(true);
      setLoading(false);
    }
  }, [mode, term]);

  const stagedList = Object.values(staged);

  // One insert for the whole batch, then one trigger. Requesting the poller
  // per change would run set_prices_from_queue.py -- and therefore a product
  // options replication -- once per edit, which is what makes a session of
  // repricing expensive in Cin7 API calls. Batching keeps it to one
  // replication however many lines were changed.
  const commit = async () => {
    if (!stagedList.length) return;
    setCommitting(true);
    setCommitMsg('');

    const { error } = await supabase.from('product_price_updates').insert(stagedList);
    if (error) {
      setCommitMsg(`Failed: ${error.message}`);
      setCommitting(false);
      return;
    }

    await supabase.from('refresh_triggers')
      .update({ requested: true, requested_at: new Date().toISOString() })
      .eq('key', 'price_updates');

    setCommitMsg(`${stagedList.length} change(s) committed — applying to Cin7 shortly.`);
    setStaged({});
    setCommitting(false);
  };

  const lowCount = rows.filter(r => r.gp_retail != null && r.gp_retail < LOW_GP).length;

  return (
    <main className="app-main">
      <div className="page-nav">
        <button className="back-btn" onClick={() => navigate('/utilities')}>← Utilities</button>
        <span className="page-nav__title">Margin Check</span>
      </div>

      <section className="section">
        <SectionHeader
          title="Margin Check"
          count={rows.length}
          onRefresh={search}
          loading={loading}
        />
        <p className="section__desc">
          Look up current Cin7 pricing and the margin each price returns. Load every line on a
          purchase order, or search a single item code. GP% is calculated as
          (1 − cost × 1.15 ÷ price), matching the price approval dialog used by the invoice loaders.
        </p>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '0 16px 12px' }}>
          <select
            value={mode}
            onChange={(e) => { setMode(e.target.value); setRows([]); setSearched(false); setNote(''); }}
            style={{ padding: '6px 10px', fontSize: 12 }}
          >
            <option value="po">PO Number</option>
            <option value="code">Item Code</option>
          </select>
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
            placeholder={mode === 'po' ? 'e.g. PO-383499 or 383499' : 'e.g. MX-77024'}
            style={{ padding: '6px 10px', fontSize: 12, minWidth: 260 }}
          />
          <button onClick={search} disabled={loading || !term.trim()} style={primaryBtn(loading || !term.trim())}>
            {loading ? 'Searching...' : 'Load'}
          </button>
          {note && <span style={{ fontSize: 12, color: '#888' }}>{note}</span>}
          {lowCount > 0 && (
            <span style={{ fontSize: 12, color: '#f87171', fontWeight: 600 }}>
              {lowCount} line(s) below {LOW_GP}% GP
            </span>
          )}
        </div>

        {(stagedList.length > 0 || commitMsg) && (
          <div style={{
            display: 'flex', gap: 12, alignItems: 'center',
            padding: '10px 16px', margin: '0 16px 12px',
            background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 6,
          }}>
            {stagedList.length > 0 && (
              <>
                <strong style={{ fontSize: 13 }}>
                  {stagedList.length} change(s) staged
                </strong>
                <span style={{ fontSize: 12, color: '#666' }}>
                  {stagedList.map(s => s.code).join(', ')}
                </span>
                <button onClick={commit} disabled={committing} style={primaryBtn(committing)}>
                  {committing ? 'Committing...' : 'Commit Changes'}
                </button>
                <button
                  className="action-btn action-btn--ghost"
                  onClick={() => { setStaged({}); setCommitMsg(''); }}
                  disabled={committing}
                >
                  Discard
                </button>
              </>
            )}
            {commitMsg && <span style={{ fontSize: 12 }}>{commitMsg}</span>}
          </div>
        )}

        <MarginTable
          rows={rows}
          loading={loading}
          searched={searched}
          staged={staged}
          onChange={setEditing}
        />

        {editing && (
          <PriceModal
            row={editing}
            staged={staged[editing.code]}
            onClose={() => setEditing(null)}
            onSaved={(code, payload) => {
              setStaged((s) => ({ ...s, [code]: payload }));
              setCommitMsg('');
              setEditing(null);
            }}
          />
        )}
      </section>
    </main>
  );
};

export default MarginCheck;
